import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { ListingHoldActorType, ListingHoldReason } from '../../../../../__generated__/graphql';
import { ICarListingStatusQuery } from '../../../../services/listingHoldService/__generated__/queries.generated';
import { Alert, Badge } from '../../../../components/ui';
import { holdBannerCopy } from '../../../../utils/listingHold';

type ListingStatus = ICarListingStatusQuery['carListingStatus'];
type Hold = NonNullable<ListingStatus['hold']>;

const Meta = styled.p`
  ${tw`text-xs mt-1`}
  color: var(--color-text-secondary);
`;

const Body = styled.p`
  ${tw`text-sm mt-1`}
`;

const CampaignList = styled.ul`
  ${tw`mt-2 flex flex-col gap-2 list-none p-0 m-0`}
`;

const CampaignItem = styled.li`
  ${tw`text-sm p-2 rounded-md`}
  background-color: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
`;

const CampaignHead = styled.div`
  ${tw`flex flex-wrap items-center gap-2 font-semibold`}
`;

const CampaignLink = styled.a`
  ${tw`underline`}
  color: inherit;
`;

/** Spec 015 US-1: why the car is unlisted, for its host and the admins who manage it. */
export function ListingHoldBanner({
  status,
  hold,
  brandName,
}: {
  status: ListingStatus;
  hold: Hold;
  brandName: string;
}) {
  const copy = holdBannerCopy(hold, {
    brandName,
    supportEmail: status.supportEmail,
    isAdminView: status.isAdminView,
  });
  const tone =
    hold.reason === ListingHoldReason.OpenRecall
      ? 'error'
      : hold.reason === ListingHoldReason.Admin
        ? 'warning'
        : 'neutral';
  const who =
    hold.actorName ??
    (hold.actorType === ListingHoldActorType.System ? 'Automatic recall check' : null);
  return (
    <Alert tone={tone} title={copy.title} data-testid="listing-hold-banner">
      <Body>{copy.body}</Body>
      <Meta>
        {[who, new Date(hold.createdAt).toLocaleDateString()].filter(Boolean).join(' · ')}
      </Meta>
      {hold.note && <Body data-testid="listing-hold-note">Note: {hold.note}</Body>}
      {hold.campaigns.length > 0 && (
        <CampaignList>
          {hold.campaigns.map(campaign => (
            <CampaignItem key={campaign.campaignNumber}>
              <CampaignHead>
                <CampaignLink href={campaign.nhtsaUrl} target="_blank" rel="noopener noreferrer">
                  {campaign.campaignNumber}
                </CampaignLink>
                {campaign.component && <span>{campaign.component}</span>}
                {campaign.parkIt && (
                  <Badge tone="error" size="sm">
                    Do not drive
                  </Badge>
                )}
                {campaign.parkOutSide && (
                  <Badge tone="error" size="sm">
                    Park outside
                  </Badge>
                )}
                {campaign.remedyAvailable === false && (
                  <Badge tone="warning" size="sm">
                    No fix yet
                  </Badge>
                )}
              </CampaignHead>
              {campaign.summary && <Body>{campaign.summary}</Body>}
              {campaign.remedy && <Meta>Fix: {campaign.remedy}</Meta>}
            </CampaignItem>
          ))}
        </CampaignList>
      )}
      {copy.evidenceLine && <Body data-testid="listing-hold-evidence">{copy.evidenceLine}</Body>}
    </Alert>
  );
}
